import { FC } from 'react';
import { Alert, Button } from 'react-daisyui';
import { Link } from 'react-router-dom';
import Routes from '@/router/Routes.ts';
import Stepper from '@/components/shared/Stepper/Stepper.tsx';
import { useStepperContext } from '@/components/shared/Stepper/StepperContext.tsx';
import * as z from 'zod';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Trans, useTranslation } from 'react-i18next';
import LoadingButton from '@/components/functional/LoadingButton/LoadingButton.tsx';
import BrandCardSideContent from '@/components/ui/BrandCardSideContent/BrandCardSideContent.tsx';
import TextInput from '@/components/functional/TextInput/TextInput.tsx';
import useRegisterUser from '@/api/useRegisterUser.ts';
import CheckboxInput from '@/components/functional/CheckboxInput/CheckboxInput.tsx';
import { passToRouteSearchParams } from '@/utils/passToRouteSearchParams.ts';
import { passwordSchema } from '$/utils/validators/passwordSchema.ts';

type Schema = SignUpStepSchema;

const steps = ['sign-up', 'success-sign-up'] as const;
const RegisterPage: FC = () => {
  return (
    <Stepper.Wrapper steps={steps} initialStep="sign-up">
      <Stepper.Step<typeof steps> step="sign-up">
        <StepSignUp />
      </Stepper.Step>
      <Stepper.Step<typeof steps> step="success-sign-up">
        <StepSuccessSignUp />
      </Stepper.Step>
    </Stepper.Wrapper>
  );
};

export default RegisterPage;

const signUpStepSchema = z
  .object({
    email: z.string().min(1, { message: 'This field is required.' }).email('Invalid email'),
    name: z.string().min(1, { message: 'This field is required.' }),
    password: passwordSchema,
    repeat_password: z.string().min(1, { message: 'This field is required.' }),
    terms: z.boolean().refine((value) => value, { message: 'You must agree to the terms and conditions' }),
  })
  .superRefine(({ repeat_password, password }, ctx) => {
    if (repeat_password !== password) {
      ctx.addIssue({
        code: 'custom',
        message: 'The passwords did not match',
        path: ['repeat_password'],
      });
    }
  });

type SignUpStepSchema = z.infer<typeof signUpStepSchema>;
function StepSignUp() {
  const { t } = useTranslation();
  const {
    mutateAsync: registerUser,
    isError: isErrorRegisterUser,
    isPending: isLoadingRegisterUser,
    error: errorRegisterUser,
  } = useRegisterUser();
  const { goToStep, setContextData } = useStepperContext<typeof steps, Schema>();
  const { control, handleSubmit } = useForm<SignUpStepSchema>({
    resolver: zodResolver(signUpStepSchema),
    defaultValues: {
      email: '',
      name: '',
      password: '',
      repeat_password: '',
      terms: false,
    },
  });

  const handleNextStep = (data: SignUpStepSchema) => {
    void (async () => {
      try {
        if (isLoadingRegisterUser) return;
        await registerUser({ ...data, user_type: 'nonprofit' });
        setContextData(data);
        goToStep('success-sign-up');
      } catch (error) {
        console.error(error);
      }
    })();
  };

  return (
    <BrandCardSideContent>
      <form className="w-full max-w-[428px] flex flex-col gap-4" onSubmit={handleSubmit(handleNextStep)}>
        <div className="flex flex-col gap-4">
          {isErrorRegisterUser && <Alert status="error">{errorRegisterUser.message}</Alert>}
          <Controller
            control={control}
            name="name"
            render={({ field: { onChange, value }, formState: { errors } }) => (
              <TextInput
                error={errors.name?.message}
                value={value}
                onChange={onChange}
                label={t('label.name')}
                placeholder={t('placeholder.name')}
              />
            )}
          />
          <Controller
            control={control}
            name="email"
            render={({ field: { onChange, value }, formState: { errors } }) => (
              <TextInput
                error={errors.email?.message}
                value={value}
                onChange={onChange}
                label={t('label.email')}
                placeholder={t('placeholder.email')}
              />
            )}
          />
          <Controller
            control={control}
            name="password"
            render={({ field: { onChange, value }, formState: { errors } }) => (
              <TextInput
                error={errors.password?.message}
                value={value}
                onChange={onChange}
                type="password"
                autoComplete="password"
                label={t('label.password')}
                placeholder={t('placeholder.password')}
              />
            )}
          />
          <Controller
            control={control}
            name="repeat_password"
            render={({ field: { onChange, value }, formState: { errors } }) => (
              <TextInput
                error={errors.repeat_password?.message}
                value={value}
                onChange={onChange}
                type="password"
                autoComplete="repeat_password"
                label={t('label.repeat_password')}
                placeholder={t('placeholder.repeat_password')}
              />
            )}
          />
          <Controller
            control={control}
            name="terms"
            render={({ field: { onChange, value }, formState: { errors } }) => (
              <CheckboxInput
                onChange={onChange}
                checked={value}
                error={errors.terms?.message}
                label={
                  <Trans i18nKey="registrationPage.agreeToTerms">
                    I accept the
                    <Link target="_blank" className="text-link underline" to={import.meta.env.VITE_TERMS_LINK}>
                      Terms and Conditions
                    </Link>
                  </Trans>
                }
              />
            )}
          />
          <LoadingButton loading={isLoadingRegisterUser} type="submit" color="neutral" className="w-full">
            {t('button.createAccount')}
          </LoadingButton>
        </div>
        <div className="flex justify-center sm:justify-end">
          <Link to={Routes.LOGIN}>
            <Button color="ghost">{t('button.loginToExistingAccount')}</Button>
          </Link>
        </div>
      </form>
    </BrandCardSideContent>
  );
}

function StepSuccessSignUp() {
  const { t } = useTranslation();
  const { contextData } = useStepperContext<typeof steps, Schema>();

  return (
    <BrandCardSideContent>
      <div className="flex flex-col justify-center items-center gap-6 sm:gap-8 w-full max-w-[343px] px-4">
        <div className="flex flex-col gap-3 sm:gap-4 items-center text-center">
          <h2 className="font-medium text-3xl sm:text-4xl md:text-5xl">{t('registrationPage.congrats')}</h2>
          <p className="text-text-secondary text-sm sm:text-base">{t('registrationPage.congratsText')}</p>
        </div>
        <Link
          className="w-full flex flex-col"
          to={passToRouteSearchParams(Routes.CONFIRM_EMAIL, {
            email: encodeURIComponent(contextData?.email as string),
          })}
        >
          <Button color="primary" className="w-full">{t('button.gotIt')}</Button>
        </Link>
      </div>
    </BrandCardSideContent>
  );
}
